import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plus, ShieldAlert, Trash2, Webhook } from 'lucide-react';
import { useState } from 'react';

import { CopyIcon } from '@/components/CopyIcon';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  getAdminWebhookEndpointsGetAdminClientsClientIdGuidWebhookEndpointsQueryKey,
  useAdminWebhookEndpointsDeleteAdminWebhookEndpointsIdGuid,
  useAdminWebhookEndpointsGetAdminClientsClientIdGuidWebhookEndpoints,
  useAdminWebhookEndpointsPostAdminClientsClientIdGuidWebhookEndpoints,
  useAdminWebhookEndpointsPostAdminWebhookEndpointsIdGuidDisable,
  useAdminWebhookEndpointsPostAdminWebhookEndpointsIdGuidEnable,
  useAdminWebhookEndpointsPostAdminWebhookEndpointsIdGuidRotateSecret,
} from '@/lib/api/admin-webhook-endpoints/admin-webhook-endpoints';
import { Badge } from '@/components/ui/badge';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useHasRole } from '@/lib/auth/useAuth';

/**
 * Well-known Adyen Checkout / Disputes event codes. Empty checkbox set means
 * "receive every event Adyen sends." Grouped for readability — the wire
 * payload is a flat string[] either way.
 */
const EventCodeGroups: { label: string; codes: string[] }[] = [
  {
    label: 'Payment lifecycle',
    codes: ['AUTHORISATION', 'CAPTURE', 'CAPTURE_FAILED', 'REFUND', 'REFUND_FAILED', 'CANCEL_OR_REFUND'],
  },
  {
    label: 'Disputes',
    codes: [
      'NOTIFICATION_OF_CHARGEBACK',
      'CHARGEBACK',
      'CHARGEBACK_REVERSED',
      'SECOND_CHARGEBACK',
      'PREARBITRATION_LOST',
      'REQUEST_FOR_INFORMATION',
    ],
  },
  {
    label: 'Reporting',
    codes: ['REPORT_AVAILABLE'],
  },
];

interface Props {
  clientId: string;
}

const CreateSchema = z.object({
  url: z.string().url('Must be an absolute http(s) URL.').max(2048),
  // Empty array means "no filter — receive every event code." Sent on the
  // wire as `eventFilter: []`; the dispatcher treats no-filter as match-all.
  eventFilter: z.array(z.string().max(64)).default([]),
  description: z.string().max(200).optional(),
});
type CreateForm = z.infer<typeof CreateSchema>;

/**
 * Per-client webhook endpoints — where the proxy delivers Adyen events
 * onward. SuperAdmin-gated for create / rotate / delete; Admin can toggle
 * enable / disable. The plaintext signing secret is shown exactly once on
 * create and on rotate; partners must copy it then because it cannot be
 * retrieved later.
 */
export function WebhookEndpointsCard({ clientId }: Props) {
  const isSuperAdmin = useHasRole('SuperAdmin');
  const isAdmin = useHasRole('Admin');
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const [createOpen, setCreateOpen] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<{ secret: string; isRotation: boolean } | null>(null);

  const list = useAdminWebhookEndpointsGetAdminClientsClientIdGuidWebhookEndpoints(clientId);

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: getAdminWebhookEndpointsGetAdminClientsClientIdGuidWebhookEndpointsQueryKey(clientId),
    });

  const createMutation = useAdminWebhookEndpointsPostAdminClientsClientIdGuidWebhookEndpoints({
    mutation: {
      onSuccess: async (data) => {
        await invalidate();
        toast.success('Webhook endpoint created.');
        setCreateOpen(false);
        setRevealedSecret({ secret: data.plaintextSigningSecret, isRotation: false });
      },
      onError: () => toast.error('Could not create webhook endpoint.'),
    },
  });

  const rotateMutation = useAdminWebhookEndpointsPostAdminWebhookEndpointsIdGuidRotateSecret({
    mutation: {
      onSuccess: async (data) => {
        await invalidate();
        toast.success('Signing secret rotated.');
        setRevealedSecret({ secret: data.plaintextSigningSecret, isRotation: true });
      },
      onError: () => toast.error('Could not rotate signing secret.'),
    },
  });

  const enableMutation = useAdminWebhookEndpointsPostAdminWebhookEndpointsIdGuidEnable({
    mutation: { onSuccess: invalidate, onError: () => toast.error('Could not enable endpoint.') },
  });
  const disableMutation = useAdminWebhookEndpointsPostAdminWebhookEndpointsIdGuidDisable({
    mutation: { onSuccess: invalidate, onError: () => toast.error('Could not disable endpoint.') },
  });

  const deleteMutation = useAdminWebhookEndpointsDeleteAdminWebhookEndpointsIdGuid({
    mutation: {
      onSuccess: async () => {
        await invalidate();
        toast.success('Webhook endpoint deleted.');
      },
      onError: () => toast.error('Could not delete endpoint.'),
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Webhook className="h-4 w-4 text-muted-foreground" />
            Webhook endpoints
          </CardTitle>
          <CardDescription>
            Where the proxy delivers Adyen notifications for this client. Each delivery is HMAC-SHA256 signed with the
            endpoint's secret.
          </CardDescription>
        </div>
        {isSuperAdmin ? (
          <Button size="sm" className="gap-2" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> Add endpoint
          </Button>
        ) : (
          <Badge variant="outline" className="gap-1">
            <ShieldAlert className="h-3 w-3" />
            SuperAdmin only
          </Badge>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {list.isLoading ? (
          <div className="space-y-2 p-6">
            {Array.from({ length: 2 }, (_, i) => (
              <div key={i} className="h-12 animate-pulse rounded bg-muted" />
            ))}
          </div>
        ) : !list.data || list.data.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No webhook endpoints configured.
            {isSuperAdmin && ' Add one to start delivering Adyen events to the partner.'}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {list.data.map((e) => (
              <li key={e.id} className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-mono text-sm">{e.url}</span>
                    {e.enabled ? (
                      <Badge variant="success">Enabled</Badge>
                    ) : (
                      <Badge variant="warning">Disabled</Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {e.eventFilter.length === 0 ? (
                      <Badge variant="outline" className="text-[10px]">
                        all events
                      </Badge>
                    ) : (
                      e.eventFilter.map((evt) => (
                        <Badge key={evt} variant="outline" className="text-[10px]">
                          {evt}
                        </Badge>
                      ))
                    )}
                  </div>
                  {e.description && <p className="text-xs text-muted-foreground">{e.description}</p>}
                  <p className="text-[11px] text-muted-foreground">
                    {e.lastDeliveredAt
                      ? `Last delivery: ${new Date(e.lastDeliveredAt).toLocaleString()}`
                      : 'No deliveries yet.'}
                    {e.lastError ? ` · last error: ${e.lastError}` : ''}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        e.enabled
                          ? disableMutation.mutate({ id: e.id })
                          : enableMutation.mutate({ id: e.id })
                      }
                      disabled={enableMutation.isPending || disableMutation.isPending}
                    >
                      {e.enabled ? 'Disable' : 'Enable'}
                    </Button>
                  )}
                  {isSuperAdmin && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1"
                      onClick={() => rotateMutation.mutate({ id: e.id })}
                      disabled={rotateMutation.isPending}
                    >
                      <KeyRound className="h-3 w-3" /> Rotate
                    </Button>
                  )}
                  {isSuperAdmin && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      onClick={async () => {
                        const ok = await confirm({
                          title: 'Delete this webhook endpoint?',
                          description: (
                            <>
                              The endpoint <code className="font-mono text-xs">{e.url}</code> will
                              be removed. Future inbound events won't fan out to this URL anymore.
                              This cannot be undone.
                            </>
                          ),
                          confirmLabel: 'Delete endpoint',
                          variant: 'destructive',
                        });
                        if (ok) deleteMutation.mutate({ id: e.id });
                      }}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <CreateEndpointDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        pending={createMutation.isPending}
        onSubmit={(values) =>
          createMutation.mutate({
            clientId,
            data: {
              url: values.url.trim(),
              eventFilter: values.eventFilter,
              description: values.description?.trim() || null,
            },
          })
        }
      />

      <SecretRevealDialog
        open={revealedSecret !== null}
        secret={revealedSecret?.secret ?? ''}
        isRotation={revealedSecret?.isRotation ?? false}
        onOpenChange={(o) => !o && setRevealedSecret(null)}
      />
    </Card>
  );
}

interface CreateEndpointDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pending: boolean;
  onSubmit: (values: CreateForm) => void;
}

function CreateEndpointDialog({ open, onOpenChange, pending, onSubmit }: CreateEndpointDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateForm>({
    resolver: zodResolver(CreateSchema),
    defaultValues: { url: '', eventFilter: [], description: '' },
  });

  const selected = watch('eventFilter') ?? [];
  const toggleEvent = (code: string, checked: boolean) => {
    const next = checked
      ? Array.from(new Set([...selected, code]))
      : selected.filter((c) => c !== code);
    setValue('eventFilter', next, { shouldDirty: true });
  };
  const selectAll = () => {
    setValue('eventFilter', EventCodeGroups.flatMap((g) => g.codes), { shouldDirty: true });
  };
  const clearAll = () => setValue('eventFilter', [], { shouldDirty: true });

  const close = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add webhook endpoint</DialogTitle>
          <DialogDescription>
            The signing secret is shown once after creation. Copy it then — it cannot be retrieved later.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="wh-url">Delivery URL</Label>
            <Input
              id="wh-url"
              autoComplete="off"
              placeholder="https://partner.example.com/webhooks/adyen"
              {...register('url')}
            />
            {errors.url && <p className="text-xs text-destructive">{errors.url.message}</p>}
          </div>
          <fieldset className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <legend className="text-sm font-medium">
                Event filter <span className="text-muted-foreground">(optional)</span>
              </legend>
              <div className="flex gap-1 text-xs">
                <button
                  type="button"
                  onClick={selectAll}
                  className="rounded px-1.5 py-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={clearAll}
                  className="rounded px-1.5 py-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  Clear
                </button>
              </div>
            </div>

            <div className="space-y-3 rounded-md border border-border p-3">
              {EventCodeGroups.map((group) => (
                <div key={group.label}>
                  <p className="mb-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
                    {group.label}
                  </p>
                  <div className="grid grid-cols-1 gap-x-3 gap-y-1.5 sm:grid-cols-2">
                    {group.codes.map((code) => {
                      const id = `wh-evt-${code}`;
                      const checked = selected.includes(code);
                      return (
                        <label
                          key={code}
                          htmlFor={id}
                          className="flex cursor-pointer items-center gap-2 text-sm"
                        >
                          <Checkbox
                            id={id}
                            checked={checked}
                            onChange={(e) => toggleEvent(code, e.currentTarget.checked)}
                          />
                          <span className="font-mono text-xs">{code}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <p className="text-[11px] text-muted-foreground">
              {selected.length === 0
                ? 'No filter — partner receives every event code Adyen sends.'
                : `${selected.length} event${selected.length === 1 ? '' : 's'} selected.`}
            </p>
          </fieldset>
          <div className="space-y-2">
            <Label htmlFor="wh-desc">Description</Label>
            <Input
              id="wh-desc"
              autoComplete="off"
              placeholder="Production reconciliation"
              {...register('description')}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => close(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || pending}>
              {pending ? 'Creating…' : 'Create endpoint'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface SecretRevealDialogProps {
  open: boolean;
  secret: string;
  isRotation: boolean;
  onOpenChange: (open: boolean) => void;
}

function SecretRevealDialog({ open, secret, isRotation, onOpenChange }: SecretRevealDialogProps) {
  const [copied, setCopied] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isRotation ? 'New signing secret' : 'Signing secret'}</DialogTitle>
          <DialogDescription>
            Copy this now — it will not be shown again. Use it to verify <code className="font-mono">X-DWPay-Signature</code>{' '}
            on incoming deliveries.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 p-3">
          <code className="flex-1 break-all font-mono text-xs">{secret}</code>
          <Button
            size="sm"
            variant="outline"
            className="gap-1"
            onClick={() => {
              navigator.clipboard.writeText(secret);
              setCopied(true);
              toast.success('Copied to clipboard.');
            }}
          >
            <CopyIcon copied={copied} onRevert={() => setCopied(false)} className="h-3 w-3" />
            Copy
          </Button>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
